import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { ReferenceType } from '@/core/graphql/generated/types';
import { formatLocalizedName } from '@/utils/formatters/formatLocalizedName';

import type { PicklistSource } from './fields/types';

export type PicklistOption = Pick<
	DropdownItemBase,
	'id' | 'name' | 'localizedName'
>;

export type ReferenceOptions = {
	-readonly [
		K in keyof typeof ReferenceType as `${Uncapitalize<K>}Options`
	]: PicklistOption[];
};

export type Picklists = ReferenceOptions & {
	yearOptions: { id: string; name: string }[];
	countryOptions: DropdownItemBase[];
	stateOptionsDictionary: Record<string, DropdownItemBase[]>;
};

export type PicklistKey = Exclude<keyof Picklists, 'stateOptionsDictionary'>;

const REFERENCE_OPTION_KEYS = Object.fromEntries(
	(Object.keys(ReferenceType) as Array<keyof typeof ReferenceType>).map(key => [
		ReferenceType[key],
		`${key.charAt(0).toLowerCase()}${key.slice(1)}Options`
	])
) as Record<ReferenceType, keyof ReferenceOptions>;

export const toPicklistKey = (source: PicklistSource): PicklistKey =>
	source === 'country' ? 'countryOptions' : REFERENCE_OPTION_KEYS[source];

export const getPicklistOptions = (
	picklists: Partial<Picklists> | undefined,
	source: PicklistSource
): PicklistOption[] => picklists?.[toPicklistKey(source)] ?? [];

export const findOptionLabel = (
	options: readonly PicklistOption[],
	id: string | null | undefined
) => {
	const option = id ? options.find(o => o.id === id) : undefined;
	return option ? formatLocalizedName(option) || undefined : undefined;
};

export const resolveOptionId = (
	options: readonly PicklistOption[],
	value: string | null | undefined
) =>
	value ? options.find(o => o.id === value || o.name === value)?.id : undefined;

export const findActiveOptionId = (options: readonly PicklistOption[]) =>
	options.find(o => o.id === 'Active' || o.name?.toLowerCase() === 'active')
		?.id;
